import type { ApiClient } from "@/modules/core/api/client/apiClient"
import { LoggerService } from "@modules/common/logger/loggerService"
import { UserDtoSchema, type UserDto } from "@/modules/profile/userProfile/userDto"
import type { SocialApiGateway } from "@/modules/social/gateway/socialApiGateway"
import {
  CommentDtoSchema,
  ReactionDtoSchema,
  SocialPostDtoSchema,
  type CommentDto,
  type CreateCommentInput,
  type ReactionDto,
  type SocialPostDto,
} from "@/modules/social/gateway/socialDto"
import type { Paginated } from "@/modules/utils/paginated"
import { z } from "zod"

export class SocialApi implements SocialApiGateway {
  private baseUrl = "/social"

  constructor(private api: ApiClient) {}

  /* ── Social Post ── */

  async getSessionSocialPost(socialPostId: string): Promise<SocialPostDto> {
    const result = await this.api.get<SocialPostDto>(`${this.baseUrl}/${socialPostId}`)
    return LoggerService.parseApiDto(SocialPostDtoSchema, result.data)
  }

  async getUserExerciseSocialPost(userId: string, exerciseId: string): Promise<SocialPostDto | null> {
    const result = await this.api.get<SocialPostDto>(`${this.baseUrl}/exercises/${exerciseId}/users/${userId}`)
    if (result.status === 204) {
      return null
    }
    return LoggerService.parseApiDto(SocialPostDtoSchema, result.data)
  }

  async createSocialPost({
    socialPostType,
    sessionId,
    userId,
    exerciseId,
  }: {
    socialPostType: "SESSION" | "EXERCISE_COMPLETION"
    sessionId?: string
    userId?: string
    exerciseId?: string
  }): Promise<SocialPostDto> {
    const result = await this.api.post<SocialPostDto>(
      `${this.baseUrl}`,
      {
        sessionId,
        userId,
        exerciseId,
      },
      {
        params: { socialPostType },
      },
    )
    return LoggerService.parseApiDto(SocialPostDtoSchema, result.data)
  }

  /* ── Comments ── */

  async getComments(socialPostId: string, page = 0, pageSize = 20): Promise<Paginated<CommentDto>> {
    const result = await this.api.get<Paginated<CommentDto>>(`${this.baseUrl}/${socialPostId}/comments`, {
      params: { page, pageSize },
    })
    return {
      ...result.data,
      content: LoggerService.parseApiDto(z.array(CommentDtoSchema), result.data.content),
    }
  }

  async getChildComments(
    socialPostId: string,
    commentId: string,
    page = 0,
    pageSize = 20,
  ): Promise<Paginated<CommentDto>> {
    const result = await this.api.get<Paginated<CommentDto>>(
      `${this.baseUrl}/${socialPostId}/comments/${commentId}/children`,
      { params: { page, pageSize } },
    )
    return {
      ...result.data,
      content: LoggerService.parseApiDto(z.array(CommentDtoSchema), result.data.content),
    }
  }

  async createComment(socialPostId: string, input: CreateCommentInput): Promise<CommentDto> {
    const result = await this.api.post<CommentDto>(`${this.baseUrl}/${socialPostId}/comments`, input)
    return LoggerService.parseApiDto(CommentDtoSchema, result.data)
  }

  async deleteComment(socialPostId: string, commentId: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/${socialPostId}/comments/${commentId}`)
  }

  /* ── Reactions on Social Post ── */

  async getReactions(socialPostId: string): Promise<ReactionDto[]> {
    const result = await this.api.get<ReactionDto[]>(`${this.baseUrl}/${socialPostId}/reactions`)
    return LoggerService.parseApiDto(z.array(ReactionDtoSchema), result.data)
  }

  async addReaction(socialPostId: string, emoji: string): Promise<void> {
    await this.api.post(`${this.baseUrl}/${socialPostId}/reactions`, { emoji })
  }

  async removeReaction(socialPostId: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/${socialPostId}/reactions`)
  }

  /* ── Reactions on Comments ── */

  async getCommentReactions(socialPostId: string, commentId: string): Promise<ReactionDto[]> {
    const result = await this.api.get<ReactionDto[]>(`${this.baseUrl}/${socialPostId}/comments/${commentId}/reactions`)
    return LoggerService.parseApiDto(z.array(ReactionDtoSchema), result.data)
  }

  async addCommentReaction(socialPostId: string, commentId: string, emoji: string): Promise<void> {
    await this.api.post(`${this.baseUrl}/${socialPostId}/comments/${commentId}/reactions`, { emoji })
  }

  async removeCommentReaction(socialPostId: string, commentId: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/${socialPostId}/comments/${commentId}/reactions`)
  }

  /* ── Mentionable Users ── */

  async getMentionableUsers(
    socialPostId: string,
    filter: string,
    page = 0,
    pageSize = 10,
  ): Promise<Paginated<UserDto>> {
    const result = await this.api.get<Paginated<UserDto>>(`${this.baseUrl}/${socialPostId}/users`, {
      params: { filter, page, size: pageSize },
    })
    return {
      ...result.data,
      content: LoggerService.parseApiDto(z.array(UserDtoSchema), result.data.content),
    }
  }
}
